import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { Check, Gift, Sparkles, ShieldCheck } from "lucide-react";
import SEO from "@/components/marketing/SEO";

const HIGHLIGHTS = [
  "7-day full-feature free trial — no card required",
  "Unlimited clients, invoices, returns, tasks",
  "GPT-5.2 AI for HSN, drafts, summaries, insights",
  "WhatsApp Connect + bulk Excel import",
  "Document vault & secure team access",
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error("Please accept terms to continue");
      return;
    }
    setLoading(true);
    try {
      await register({ ...form, role: "admin" });
      toast.success("Welcome — your 7-day free trial has started");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <SEO
        title="Start your free 7-day trial — TAXINPHO"
        description="Create your TAXINPHO account and start managing your CA practice in 30 seconds. 7-day free trial. No credit card required."
        canonical="https://taxinpho.co/register"
        noindex
      />
      {/* Left side — trial pitch */}
      <div className="hidden lg:flex flex-1 bg-zinc-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30"
             style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #002FA7 0%, transparent 50%), radial-gradient(circle at 80% 70%, #1ABEDC 0%, transparent 50%)" }} />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Logo className="h-32 w-auto" variant="stacked" />

          <div>
            <div className="inline-flex items-center gap-2 bg-[#1ABEDC] text-zinc-950 px-3 py-1 text-xs font-bold mb-6">
              <Gift size={14} /> 7-DAY FREE TRIAL
            </div>
            <h1 className="font-display font-black text-5xl tracking-tighter leading-[0.95] mb-6">
              Start your CA office<br />in 30 seconds.
            </h1>
            <p className="text-zinc-300 max-w-md leading-relaxed mb-8">
              Try every feature for 7 days, no credit card. After the trial,
              keep going for ₹999/month or ₹9,999/year. Cancel anytime.
            </p>

            <ul className="space-y-2.5">
              {HIGHLIGHTS.map((h) => (
                <li key={h} className="flex items-start gap-2 text-sm">
                  <Check size={16} className="text-[#1ABEDC] mt-0.5 flex-shrink-0" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center gap-4 text-xs text-zinc-400">
            <ShieldCheck size={14} /> <span>Secure Razorpay payments · UPI · Cards · NetBanking</span>
          </div>
        </div>
      </div>

      {/* Right side - form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-10">
            <Logo className="h-12 w-auto" />
          </div>
          <div className="inline-flex items-center gap-1.5 bg-[#EFF4FF] text-[#002FA7] px-2.5 py-1 text-xs font-bold mb-3">
            <Sparkles size={12} /> 7-DAY FREE TRIAL
          </div>
          <h2 className="font-display font-black text-3xl tracking-tighter mb-2 text-zinc-900">
            Set up your office.
          </h2>
          <p className="text-zinc-600 text-sm mb-8">First user becomes the office Admin. No card required for trial.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="label-caps block mb-2">Full Name</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="register-name"
                className="w-full border border-zinc-300 px-3 py-2.5 focus:outline-none focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900"
                placeholder="CA Ramesh Kumar"
              />
            </div>
            <div>
              <label className="label-caps block mb-2">Work Email</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="register-email"
                className="w-full border border-zinc-300 px-3 py-2.5 focus:outline-none focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900"
                placeholder="you@firm.com"
              />
            </div>
            <div>
              <label className="label-caps block mb-2">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="register-password"
                className="w-full border border-zinc-300 px-3 py-2.5 focus:outline-none focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900"
                placeholder="6+ characters"
              />
            </div>

            <label className="flex items-start gap-2 text-xs text-zinc-600 cursor-pointer">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                data-testid="register-accept"
                className="mt-0.5"
              />
              <span>
                I agree to start a 7-day free trial. After the trial,
                I&apos;ll be asked to subscribe (₹999/month or ₹9,999/year).
                I can cancel anytime.
              </span>
            </label>

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit"
              className="w-full bg-zinc-900 text-white py-3 font-semibold hover:bg-zinc-800 active:translate-y-[1px] transition-all disabled:opacity-60"
            >
              {loading ? "Creating..." : "Start free trial →"}
            </button>
          </form>

          <p className="mt-8 text-sm text-zinc-600">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-zinc-900 underline underline-offset-4" data-testid="link-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
